import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Framework widget-defaults API surface (T-0027 / T-0114).
 *
 * The widget-defaults editor modal reads + writes a FrameworkDefinition's
 * `widget_defaults` JSONB. Backed by `apps/api/src/routes/framework-defaults.ts`:
 *
 *   - GET /api/v1/framework-defaults/:framework_definition_id  → stored + inferred
 *   - PUT /api/v1/framework-defaults/:framework_definition_id  → full replace
 *
 * The `id` here is the FrameworkDefinition row id (i.e. the published
 * template version's `framework_definition_id`), NOT the methodology
 * template id. The library card resolves the right id before triggering
 * the editor (see `apps/web/src/components/settings/framework-card.tsx`).
 */

export const WidgetTypeSchema = z.enum([
  'text',
  'string_chip',
  'enum_chip',
  'number',
  'integer',
  'boolean_toggle',
  'date',
  'timestamp',
  'uuid_link',
]);
export type WidgetType = z.infer<typeof WidgetTypeSchema>;

export const WidgetBindingSchema = z.object({
  field: z.string(),
  widget: WidgetTypeSchema,
});
export type WidgetBinding = z.infer<typeof WidgetBindingSchema>;

export const FilterBindingSchema = z.object({
  field: z.string(),
  value: z.union([z.string(), z.number(), z.boolean()]),
});
export type FilterBinding = z.infer<typeof FilterBindingSchema>;

export const WidgetDefaultsSchema = z.object({
  top_row: z.array(WidgetBindingSchema),
  main_view: WidgetBindingSchema,
  default_filter: FilterBindingSchema.optional(),
});
export type WidgetDefaults = z.infer<typeof WidgetDefaultsSchema>;

export const FrameworkDefaultsResultSchema = z.object({
  framework_definition_id: z.string(),
  stored: WidgetDefaultsSchema.nullable(),
  inferred_defaults: WidgetDefaultsSchema,
});
export type FrameworkDefaultsResult = z.infer<typeof FrameworkDefaultsResultSchema>;

export function getFrameworkDefaults(
  frameworkDefinitionId: string,
): Promise<FrameworkDefaultsResult> {
  return apiClient(
    `/api/v1/framework-defaults/${encodeURIComponent(frameworkDefinitionId)}`,
    FrameworkDefaultsResultSchema,
  );
}

export function useFrameworkDefaultsQuery(
  frameworkDefinitionId: string | undefined,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['framework-defaults', frameworkDefinitionId] as const,
    queryFn: () => getFrameworkDefaults(frameworkDefinitionId!),
    enabled: Boolean(frameworkDefinitionId) && (opts?.enabled ?? true),
    staleTime: 60_000,
  });
}

const SetFrameworkDefaultsResponseSchema = z.object({
  framework_definition_id: z.string(),
  stored: WidgetDefaultsSchema,
});

export function setFrameworkDefaults(
  frameworkDefinitionId: string,
  body: WidgetDefaults,
): Promise<z.infer<typeof SetFrameworkDefaultsResponseSchema>> {
  return apiClient(
    `/api/v1/framework-defaults/${encodeURIComponent(frameworkDefinitionId)}`,
    SetFrameworkDefaultsResponseSchema,
    { method: 'PUT', body },
  );
}

export function useSetFrameworkDefaultsMutation(frameworkDefinitionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WidgetDefaults) => {
      if (!frameworkDefinitionId) {
        return Promise.reject(new Error('frameworkDefinitionId required'));
      }
      return setFrameworkDefaults(frameworkDefinitionId, body);
    },
    onSuccess: () => {
      if (frameworkDefinitionId) {
        void qc.invalidateQueries({
          queryKey: ['framework-defaults', frameworkDefinitionId],
        });
      }
    },
  });
}
